import React, { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useSelector, useDispatch } from "react-redux";
import { LoadingCircleOverlay } from "../../components";
import { BASKET_SET } from "../../redux/contants/constants";
import { storePendingOrder } from "../../firebase/functions";
import PaypalLogo from "../../assets/Paypallogo.webp"; // Adjust path if needed
import GooglePayLogo from "../../assets/googlepaylogo.webp"; // Add this import

// Import icons
import { FaCreditCard, FaApple, FaLock } from "react-icons/fa";
import { SiKlarna } from "react-icons/si";
import {
  getCurrencySymbol,
  getSelectedCountry,
} from "../../functions/CountryFunctions";

const Payment = ({ onSubmit, totalPrice }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("card");
  const basket = useSelector((state) => state.basket.basket);
  const shippingInfo = useSelector((state) => state.shipping.shipping);

  const currency = getSelectedCountry();
  const currencySymbol = getCurrencySymbol(currency);

  useEffect(() => {
    if (!elements) return;
    const paymentElement = elements.getElement("payment");
    if (!paymentElement) return;

    const handler = (event) => {
      setSelectedMethod(event.value.type || event.value);
    };

    paymentElement.on("change", handler);

    return () => {
      paymentElement.off("change", handler);
    };
  }, [elements]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    const orderId = localStorage.getItem("orderId");
    await storePendingOrder(
      basket,
      shippingInfo,
      orderId,
      currency,
      currencySymbol,
    );

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url:
          window.location.origin + "/order-complete" + `?orderId=${orderId}`,
      },
    });

    if (!error && paymentIntent) {
      setErrorMsg("");
      dispatch({ type: BASKET_SET, basket: [] });
      setLoading(false);
      onSubmit();
    } else {
      setErrorMsg(error.message);
      setLoading(false);
    }
  };

  // Helper to select the correct icon
  const getPayIcon = (method) => {
    switch (method) {
      case "paypal":
        return (
          <img
            src={PaypalLogo}
            alt="PayPal"
            style={{ height: "24px", width: "auto" }}
          />
        );
      case "google_pay":
      case "googlepay":
        return (
          <img
            src={GooglePayLogo}
            alt="Google Pay"
            style={{ height: "24px", width: "auto" }}
          />
        );
      case "klarna":
        return <SiKlarna size={24} />;
      case "apple_pay":
      case "applepay":
        return <FaApple size={20} />;
      default:
        return <FaCreditCard size={20} />;
    }
  };

  // Unified button styling
  const buttonStyle = {
    opacity: !stripe || loading ? 0.5 : 1,
    cursor: !stripe || loading ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff",
    color: "#111",
    border: "1px solid #ddd",
    borderRadius: "5px", // <-- updated border radius
  };

  // Optionally, override background for specific methods
  if (selectedMethod === "paypal") {
    buttonStyle.background = "#ffc439";
    buttonStyle.border = "none";
  } else if (selectedMethod === "klarna") {
    buttonStyle.background = "#ffb3c7";
    buttonStyle.border = "none";
  } else if (selectedMethod === "apple_pay" || selectedMethod === "applepay") {
    buttonStyle.background = "#000";
    buttonStyle.color = "#fff";
    buttonStyle.border = "none";
  } else if (
    selectedMethod === "google_pay" ||
    selectedMethod === "googlepay"
  ) {
    buttonStyle.background = "#fff";
    buttonStyle.border = "1px solid #4285f4";
  } else if (selectedMethod === "card") {
    buttonStyle.background = "var(--color-subheading)"; // <-- your variable
    buttonStyle.color = "#fff";
    buttonStyle.border = "none";
  }

  return (
    <form className="payment-form" onSubmit={handleSubmit}>
      {loading && <LoadingCircleOverlay />}
      <h2 className="payment-title">Payment Information</h2>
      <div style={{ width: "100%", maxWidth: "600px", margin: "0 auto" }}>
        <PaymentElement
          options={{
            layout: {
              type: "accordion",
              defaultCollapsed: false,
              radios: "never",
              spacedAccordionItems: true,
            },
          }}
        />
      </div>

      {errorMsg && (
        <div
          className="payment-error"
          style={{ height: "32px", width: "100%" }}
        >
          <span
            style={{ color: "#d32f2f", textAlign: "center", display: "block" }}
          >
            {errorMsg}
          </span>
        </div>
      )}

      <div className="payment-total">
        <h1>Total</h1>
        <h1>
          {currencySymbol}
          {(totalPrice ?? 0).toFixed(2)}
        </h1>
      </div>
      <button
        type="submit"
        className="payment-btn"
        disabled={!stripe || loading}
        style={buttonStyle}
      >
        {getPayIcon(selectedMethod)}
        {selectedMethod === "paypal"
          ? ""
          : selectedMethod === "google_pay" || selectedMethod === "googlepay"
            ? `Pay with Google Pay`
            : selectedMethod === "klarna"
              ? `Pay with Klarna`
              : selectedMethod === "apple_pay" || selectedMethod === "applepay"
                ? `Pay with Apple Pay`
                : `Pay ${currencySymbol}${(totalPrice ?? 0).toFixed(2)}`}
      </button>
      <div className="flex-row-center mt-sm">
        <p className="mt-md" style={{ fontSize: "11px", color: "#666" }}>
          <FaLock style={{ marginRight: "8px" }} />
          Secure Checkout
        </p>
      </div>
    </form>
  );
};

export default Payment;
